import { Button, HStack, Input, Stack, Text, Textarea } from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useMemo, useRef, useState } from "react";
import { MdDataObject } from "react-icons/md";
import { getThemeColor } from "theme/colors";

interface PromptEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
}

// The inbound webhook JSON body is exposed to the prompt as {{payload.<path>}}
// tokens, substituted at trigger time. When the author pastes a sample payload
// (or the prompt already references tokens) we derive the chip list from that;
// otherwise we fall back to these common presets.
const PRESET_PATHS = [
  "payload.message",
  "payload.id",
  "payload.event",
  "payload.data",
];

// Only object keys / tokens built from these characters can be expressed as a
// backend-resolvable path segment ({{payload.a.b[0]}}); anything else (spaces,
// hyphens) is skipped so we never generate a token that won't substitute.
const SEGMENT_RE = /^[A-Za-z0-9_]+$/;
const TOKEN_RE = /\{\{\s*payload\.([A-Za-z0-9_.[\]]+)\s*\}\}/g;

const MAX_PATHS = 40;
const MAX_DEPTH = 6;

// Walk a parsed sample payload and collect every scalar leaf as a
// {{payload.*}}-style dotted path. Arrays use [0] as the representative item,
// mirroring the backend ResolvePath accessor syntax (items[0].name).
const collectSamplePaths = (root: unknown): string[] => {
  const out: string[] = [];
  const walk = (node: unknown, path: string, depth: number) => {
    if (out.length >= MAX_PATHS || depth > MAX_DEPTH) return;
    if (Array.isArray(node)) {
      if (node.length > 0) walk(node[0], `${path}[0]`, depth + 1);
      return;
    }
    if (node !== null && typeof node === "object") {
      for (const key of Object.keys(node)) {
        if (out.length >= MAX_PATHS) break;
        if (!SEGMENT_RE.test(key)) continue;
        walk(
          (node as Record<string, unknown>)[key],
          `${path}.${key}`,
          depth + 1,
        );
      }
      return;
    }
    out.push(path); // scalar leaf
  };
  walk(root, "payload", 0);
  return out;
};

// Pull the distinct payload paths already referenced by the prompt so edit mode
// surfaces the relevant fields even before a sample is pasted.
const collectTokenPaths = (text: string): string[] => {
  const found = new Set<string>();
  for (const m of text.matchAll(TOKEN_RE)) found.add(`payload.${m[1]}`);
  return [...found];
};

/**
 * Enhanced prompt textarea with a {{payload.*}} variable inserter. The chip list
 * is derived from a pasted sample payload (its leaf fields become clickable
 * variables) or, failing that, from tokens already in the prompt — falling back
 * to a small preset list. Tokens are spliced at the current caret position
 * (append when the textarea is unfocused), so authors never type mustaches.
 */
const PromptEditor = ({
  value,
  onChange,
  placeholder,
  rows = 8,
  disabled,
}: PromptEditorProps) => {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [customPath, setCustomPath] = useState("");
  const [sampleJson, setSampleJson] = useState("");

  const sample = useMemo(() => {
    const trimmed = sampleJson.trim();
    if (!trimmed) return { status: "empty" as const };
    try {
      return {
        status: "ok" as const,
        paths: collectSamplePaths(JSON.parse(trimmed)),
      };
    } catch {
      return { status: "invalid" as const };
    }
  }, [sampleJson]);

  const tokenPaths = useMemo(() => collectTokenPaths(value), [value]);

  const fromSample = sample.status === "ok" && sample.paths.length > 0;
  const chips = fromSample
    ? sample.paths
    : tokenPaths.length > 0
      ? tokenPaths
      : PRESET_PATHS;

  const insertToken = (path: string) => {
    const cleaned = path.trim().replace(/^payload\./, "");
    if (!cleaned) return;
    const token = `{{payload.${cleaned}}}`;
    const el = textareaRef.current;
    if (!el) {
      onChange(`${value}${token}`);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    onChange(next);
    // Restore the caret just after the inserted token on the next frame.
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + token.length;
      el.setSelectionRange(caret, caret);
    });
  };

  const insertCustom = () => {
    insertToken(customPath);
    setCustomPath("");
  };

  return (
    <Stack gap={2}>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          onChange(e.target.value)
        }
        placeholder={placeholder}
        rows={rows}
        resize="vertical"
        disabled={disabled}
        fontFamily="var(--font-mono)"
      />

      <Text fontSize="xs" color={getThemeColor("text.secondary")}>
        {t("app.autonomousAgents.promptEditor.hint")}
      </Text>

      {/* Sample payload → derives the insertable field chips below */}
      <Stack gap={1}>
        <Text fontSize="xs" fontWeight="medium">
          {t("app.autonomousAgents.promptEditor.sampleLabel")}
        </Text>
        <Textarea
          value={sampleJson}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setSampleJson(e.target.value)
          }
          placeholder={t("app.autonomousAgents.promptEditor.samplePlaceholder")}
          rows={4}
          resize="vertical"
          disabled={disabled}
          fontFamily="var(--font-mono)"
          fontSize="xs"
        />
        {sample.status === "invalid" ? (
          <Text fontSize="xs" color={getThemeColor("alert.error.text")}>
            {t("app.autonomousAgents.promptEditor.sampleInvalid")}
          </Text>
        ) : sample.status === "ok" && sample.paths.length === 0 ? (
          <Text fontSize="xs" color={getThemeColor("text.secondary")}>
            {t("app.autonomousAgents.promptEditor.sampleEmpty")}
          </Text>
        ) : (
          <Text fontSize="xs" color={getThemeColor("text.secondary")}>
            {t("app.autonomousAgents.promptEditor.sampleHint")}
          </Text>
        )}
      </Stack>

      {/* Insertable field chips (from sample, then prompt tokens, then presets) */}
      <Stack gap={1}>
        {fromSample && (
          <Text fontSize="xs" color={getThemeColor("text.secondary")}>
            {t("app.autonomousAgents.promptEditor.sampleFieldsLabel")}
          </Text>
        )}
        <HStack gap={2} wrap="wrap">
          {chips.map((p) => (
            <Button
              key={p}
              intent="ghost"
              size="xs"
              onClick={() => insertToken(p)}
              disabled={disabled}
            >
              <MdDataObject />
              {`{{${p}}}`}
            </Button>
          ))}
        </HStack>
      </Stack>

      <HStack gap={2}>
        <Input
          size="sm"
          placeholder={t(
            "app.autonomousAgents.promptEditor.variablePathPlaceholder",
          )}
          value={customPath}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setCustomPath(e.target.value)
          }
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter") {
              e.preventDefault();
              insertCustom();
            }
          }}
          disabled={disabled}
        />
        <Button
          intent="secondary"
          size="sm"
          onClick={insertCustom}
          disabled={disabled || !customPath.trim()}
        >
          {t("app.autonomousAgents.promptEditor.insertVariable")}
        </Button>
      </HStack>
    </Stack>
  );
};

export default PromptEditor;
